import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  KnowledgeData,
  KnowledgeService,
  DetailedClaimStatementData,
  RateData,
  RechnungData,
  StandortData,
  TitelData,
  ZahlungData,
} from './knowledge.service';
import { I18nService } from './i18n.service';

export interface PaidBookedDiagnostic {
  paymentRowsCount: number;
  paymentRowsSum: number;
  fallbackFromClaim: number;
  selectedPaidBooked: number;
  selectedSource: 'payments' | 'claim_difference';
  deltaToFallback: number;
}

function toSafeNumber(value: unknown): number {
  const parsed = parseFlexibleNumber(value);
  return parsed ?? 0;
}

function parseFlexibleNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.trim();
    if (normalized === '') {
      return null;
    }

    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    const decimal = lastComma > lastDot ? ',' : '.';
    const withoutThousands = decimal === ','
      ? normalized.replace(/\./g, '').replace(',', '.')
      : normalized.replace(/,/g, '');
    const parsed = Number.parseFloat(withoutThousands);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

export function sumBookedPayments(payments: ZahlungData[]): number {
  return payments.reduce((sum, payment) => sum + toSafeNumber(payment.zahlungsbetrag), 0);
}

export function resolvePaidBookedAmount(totalClaim: number, outstandingAmount: number, payments: ZahlungData[]): number {
  if (payments.length > 0) {
    return sumBookedPayments(payments);
  }

  return Math.max(totalClaim - outstandingAmount, 0);
}

export function diagnosePaidBookedConsistency(totalClaim: number, outstandingAmount: number, payments: ZahlungData[]): PaidBookedDiagnostic {
  const paymentRowsSum = sumBookedPayments(payments);
  const fallbackFromClaim = Math.max(totalClaim - outstandingAmount, 0);
  const selectedSource = payments.length > 0 ? 'payments' : 'claim_difference';
  const selectedPaidBooked = selectedSource === 'payments' ? paymentRowsSum : fallbackFromClaim;

  return {
    paymentRowsCount: payments.length,
    paymentRowsSum,
    fallbackFromClaim,
    selectedPaidBooked,
    selectedSource,
    deltaToFallback: selectedPaidBooked - fallbackFromClaim,
  };
}

export interface DashboardInvoice {
  number: string;
  date: string;
  amount: number;
  reason: string;
}

export type DashboardInvoiceAmountMode = 'open' | 'hidden_inconsistent';

export interface DashboardInvoicePresentation {
  amountMode: DashboardInvoiceAmountMode;
  sectionLabel: string;
  sectionLabelKey?: string;
  amountLabel: string;
  amountLabelKey?: string;
  note: string;
  noteKey?: string;
}

export interface DashboardTitleDetail {
  designation: string;
  number: string;
  court: string;
  date: string;
  amount: number;
  mainClaim: number;
  costs: number;
  interest: number;
}

export interface DashboardDetailedClaimLine {
  description: string;
  amount: number | null;
  amountText: string;
  date: string;
  mainClaim?: number | null;
  mainClaimText?: string;
  interest?: number | null;
  interestText?: string;
  nonInterestCosts?: number | null;
  nonInterestCostsText?: string;
  interestCosts?: number | null;
  interestCostsText?: string;
  claimAmount?: number | null;
  claimAmountText?: string;
  payment?: number | null;
  paymentText?: string;
  hasStructuredDetails?: boolean;
  raw?: Record<string, unknown>;
}

export interface DashboardPayment {
  amount: number;
  date: string;
  reference: string;
}

export interface DashboardBreakdown {
  mainClaim: number;
  mainClaimLabelKey: string;
  interest: number;
  costs: number;
  paidBooked: number;
  payments: DashboardPayment[];
  invoices: DashboardInvoice[];
  invoicePresentation: DashboardInvoicePresentation;
  titleAware: boolean;
  titleDetails: DashboardTitleDetail[];
  detailedStatement: DashboardDetailedClaimLine[];
}

export type DashboardProcedureStatus = 'vorgerichtlich' | 'nachgerichtlich' | 'gerichtlich' | 'ueberwachung' | 'unknown';

export interface DashboardClaim {
  aktenzeichen: string;
  creditorName: string;
  outstandingAmount: number;
  procedureStatus: DashboardProcedureStatus;
  procedureLabel: string;
  breakdown: DashboardBreakdown;
  activeInstallmentPlan: DashboardActiveInstallmentPlan | null;
}

export interface DashboardPermissions {
  installmentAllowed: boolean;
  settlementAllowed: boolean;
  deferralAllowed: boolean;
}

export interface DashboardInstallmentSnapshot {
  amount: number;
  date: string;
}

export interface DashboardActiveInstallmentPlan {
  openInstallments: number;
  installments: Array<DashboardInstallmentSnapshot & { labelKey: string; labelParams?: { index: number } }>;
}

export interface DashboardStatusDetails {
  isPrivatePerson: boolean;
  officeName: string;
  officePhone: string;
  officeEmail: string;
  officeWebsite: string;
  bankName: string;
  bankIban: string;
  bankBic: string;
  currentInstallment: DashboardInstallmentSnapshot | null;
  nextInstallment: DashboardInstallmentSnapshot | null;
  latestPaymentAmount: number;
  latestPaymentDate: string;
}

export interface DashboardFaqItem {
  id: string;
}

export interface DashboardViewModel {
  claim: DashboardClaim;
  permissions: DashboardPermissions;
  statusDetails: DashboardStatusDetails;
  faqItems: DashboardFaqItem[];
}

const defaultViewModel = (i18n: I18nService): DashboardViewModel => ({
  claim: {
    aktenzeichen: i18n.t('p1.unknown'),
    creditorName: i18n.t('p1.unknown'),
    outstandingAmount: 0,
    procedureStatus: 'unknown',
    procedureLabel: i18n.t('p1.unknown'),
    breakdown: {
      mainClaim: 0,
      mainClaimLabelKey: 'claim.mainClaim',
      interest: 0,
      costs: 0,
      paidBooked: 0,
      payments: [],
      invoices: [],
      invoicePresentation: {
        amountMode: 'open',
        sectionLabel: i18n.t('p1.partialInvoices'),
        sectionLabelKey: 'p1.partialInvoices',
        amountLabel: i18n.t('p1.openAmount'),
        amountLabelKey: 'p1.openAmount',
        note: '',
      },
      titleAware: false,
      titleDetails: [],
      detailedStatement: [],
    },
    activeInstallmentPlan: null,
  },
  permissions: {
    installmentAllowed: false,
    settlementAllowed: false,
    deferralAllowed: false,
  },
  statusDetails: {
    isPrivatePerson: false,
    officeName: 'Creditreform',
    officePhone: i18n.t('p1.notAvailable'),
    officeEmail: i18n.t('p1.notAvailable'),
    officeWebsite: i18n.t('p1.notAvailable'),
    bankName: i18n.t('p1.notAvailable'),
    bankIban: i18n.t('p1.notAvailable'),
    bankBic: i18n.t('p1.notAvailable'),
    currentInstallment: null,
    nextInstallment: null,
    latestPaymentAmount: 0,
    latestPaymentDate: '',
  },
  faqItems: [
    {
      id: 'overview',
    },
    {
      id: 'payment',
    },
    {
      id: 'contact',
    },
    {
      id: 'consequences',
    },
    {
      id: 'costs',
    },
  ],
});

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly knowledgeService = inject(KnowledgeService);
  private readonly i18n = inject(I18nService);
  private readonly knowledge = signal<KnowledgeData | null>(null);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly blocked = signal(false);
  readonly caseHeader = signal<{ aktenzeichen?: string; glaeubiger?: string } | null>(null);
  readonly viewModel = computed<DashboardViewModel>(() => {
    this.i18n.language();
    const knowledge = this.knowledge();
    return knowledge ? this.mapKnowledgeToViewModel(knowledge) : defaultViewModel(this.i18n);
  });

  readonly isReady = computed(() => !this.loading() && this.error() === null);

  load(onReloginRequired?: (message?: string) => void): void {
    this.loading.set(true);
    this.error.set(null);
    this.blocked.set(false);
    this.caseHeader.set(null);

    this.knowledgeService.getKnowledge().subscribe({
      next: (knowledge) => {
        this.knowledge.set(knowledge);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        if (this.apiErrorCode(error) === 'SESSION_REPLACED') {
          onReloginRequired?.(this.apiErrorMessage(error));
          this.loading.set(false);
          return;
        }

        this.knowledge.set(null);
        this.blocked.set(this.apiErrorCode(error) === 'SELF_SERVICE_BLOCKED');
        this.caseHeader.set(this.blocked() ? this.apiCaseHeader(error) : null);
        this.error.set(['VALIDATION_FAILED', 'KNOWLEDGE_NOT_FOUND'].includes(this.apiErrorCode(error) ?? '')
          ? this.apiErrorMessage(error) ?? this.i18n.t('p1.dashboardLoadError')
          : this.i18n.t('p1.dashboardLoadError'));
        this.loading.set(false);
      },
    });
  }

  private apiErrorCode(error: unknown): string | null {
    const envelope = error instanceof HttpErrorResponse ? error.error : error;
    if (!envelope || typeof envelope !== 'object') {
      return null;
    }

    const body = envelope as { code?: unknown; error?: unknown };
    if (typeof body.code === 'string') {
      return body.code;
    }
    if (body.error && typeof body.error === 'object') {
      const nested = body.error as { code?: unknown };
      if (typeof nested.code === 'string') {
        return nested.code;
      }
    }

    return null;
  }

  private apiCaseHeader(error: unknown): { aktenzeichen?: string; glaeubiger?: string } | null {
    const envelope = error instanceof HttpErrorResponse ? error.error : error;
    const header = envelope && typeof envelope === 'object' ? (envelope as { case_header?: unknown }).case_header : null;
    if (!header || typeof header !== 'object') {
      return null;
    }

    const text = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value : undefined);
    const { aktenzeichen, glaeubiger } = header as { aktenzeichen?: unknown; glaeubiger?: unknown };
    const result = { aktenzeichen: text(aktenzeichen), glaeubiger: text(glaeubiger) };
    return result.aktenzeichen || result.glaeubiger ? result : null;
  }

  private apiErrorMessage(error: unknown): string | undefined {
    const envelope = error instanceof HttpErrorResponse ? error.error : error;
    if (!envelope || typeof envelope !== 'object') {
      return undefined;
    }

    const body = envelope as { message?: unknown; error?: unknown };
    if (typeof body.message === 'string' && body.message.trim() !== '') {
      return body.message;
    }
    if (body.error && typeof body.error === 'object') {
      const nested = body.error as { message?: unknown };
      if (typeof nested.message === 'string' && nested.message.trim() !== '') {
        return nested.message;
      }
    }

    return undefined;
  }

  private mapKnowledgeToViewModel(knowledge: KnowledgeData): DashboardViewModel {
    const akte = this.resolveAkte(knowledge);
    const permissions = this.resolvePermissions(knowledge, akte);
    const invoices = this.mapInvoices(knowledge.rechnungen ?? []);
    const payments = knowledge.zahlungen ?? [];
    const dashboardPayments = this.mapPayments(payments);
    const rates = knowledge.raten ?? [];
    const titleDetails = this.mapTitleDetails(knowledge.titel ?? []);
    const detailedStatement = this.mapDetailedStatement(knowledge, akte);
    const office = this.resolveStandort(knowledge.standort);

    const outstandingAmount = this.readNumber(akte, ['gesamt_saldo', 'gesamtSaldo', 'saldo_gesamt', 'outstanding_amount']);
    const openMainClaim = this.readNumber(akte, ['hauptforderung_saldo', 'hauptforderungSaldo', 'main_claim', 'mainClaim']);
    const interestHf = this.readNumber(akte, ['zinsen_hf_saldo', 'zinsenHfSaldo', 'interest_hf']);
    const interestCosts = this.readNumber(akte, ['zinsen_kosten_saldo', 'zinsenKostenSaldo', 'interest_costs']);
    const costsUnverz = this.readNumber(akte, ['unverz_kosten_saldo', 'unverzKostenSaldo', 'costs_unverz']);
    const costsVerz = this.readNumber(akte, ['verz_kosten_saldo', 'verzKostenSaldo', 'costs_verz']);
    const interestClaim = this.readNumber(akte, ['zinsen_hf_forderung', 'zinsenHfForderung'])
      + this.readNumber(akte, ['zinsen_kosten_forderung', 'zinsenKostenForderung']);

    const totalClaim = this.readNumber(akte, ['gesamt_forderung', 'gesamtForderung', 'claim_total']);
    const paidBooked = resolvePaidBookedAmount(totalClaim, outstandingAmount, payments);
    const titleAware = titleDetails.some((title) => title.mainClaim > 0 || title.costs > 0 || title.interest > 0);
    const titleAmount = titleDetails.reduce((sum, title) => sum + title.amount, 0);
    const titleInterest = titleDetails.reduce((sum, title) => sum + title.interest, 0);
    const titleCosts = titleDetails.reduce((sum, title) => sum + title.costs, 0);
    const mainClaim = titleAware ? titleAmount : openMainClaim;
    const interest = titleAware ? this.clampMoney(interestClaim - titleInterest) : interestHf + interestCosts;
    const costs = titleAware ? this.clampMoney(costsUnverz + costsVerz - titleCosts) : costsUnverz + costsVerz;
    const invoicePresentation = this.resolveInvoicePresentation(openMainClaim, invoices);

    const procedureRaw = this.readString(akte, ['stadium', 'verfahren_status', 'procedure_status']);
    const procedureStatus = this.normalizeProcedureStatus(procedureRaw);

    const latestPayment = this.latestPayment(payments);
    const currentInstallment = this.installmentSnapshot(rates[0], 'aktuelle_rate_hoehe', 'aktuelle_rate_datum');
    const nextInstallment = this.installmentSnapshot(rates[0], 'naechste_rate_hoehe', 'naechste_rate_datum');

    return {
      claim: {
        aktenzeichen: this.readString(akte, ['aktennummer', 'aktenzeichen', 'case_number']) || this.i18n.t('p1.unknown'),
        creditorName: this.readString(akte, ['gl_name', 'glaeubiger', 'creditor_name']) || this.i18n.t('p1.unknown'),
        outstandingAmount,
        procedureStatus,
        procedureLabel: this.procedureLabel(procedureStatus),
        breakdown: {
          mainClaim,
          mainClaimLabelKey: titleAware ? 'claim.titleAmount' : 'claim.mainClaim',
          interest,
          costs,
          paidBooked,
          payments: dashboardPayments,
          invoices,
          invoicePresentation,
          titleAware,
          titleDetails,
          detailedStatement,
        },
        activeInstallmentPlan: this.activeInstallmentPlan(rates[0]),
      },
      permissions,
      statusDetails: {
        isPrivatePerson: this.readBoolean(akte, ['privatperson', 'is_private_person']),
        officeName: office.name,
        officePhone: office.telefon,
        officeEmail: office.email,
        officeWebsite: office.webseite,
        bankName: office.bankName,
        bankIban: office.bankIban,
        bankBic: office.bankBic,
        currentInstallment,
        nextInstallment,
        latestPaymentAmount: latestPayment.amount,
        latestPaymentDate: latestPayment.date,
      },
      faqItems: defaultViewModel(this.i18n).faqItems,
    };
  }

  private resolveAkte(knowledge: KnowledgeData): Record<string, unknown> {
    if (knowledge.akte) {
      return knowledge.akte as unknown as Record<string, unknown>;
    }

    const root = knowledge as unknown as Record<string, unknown>;
    const fallback = root['akte'] ?? root['claim'] ?? root['mockData'];
    return this.asRecord(fallback);
  }

  private resolveStandort(standort: StandortData | undefined): {
    name: string;
    telefon: string;
    email: string;
    webseite: string;
    bankName: string;
    bankIban: string;
    bankBic: string;
  } {
    if (!standort) {
      return {
        name: 'Creditreform',
        telefon: this.i18n.t('p1.notAvailable'),
        email: this.i18n.t('p1.notAvailable'),
        webseite: this.i18n.t('p1.notAvailable'),
        bankName: this.i18n.t('p1.notAvailable'),
        bankIban: this.i18n.t('p1.notAvailable'),
        bankBic: this.i18n.t('p1.notAvailable'),
      };
    }

    return {
      name: standort.name || 'Creditreform',
      telefon: standort.telefon || this.i18n.t('p1.notAvailable'),
      email: standort.email || this.i18n.t('p1.notAvailable'),
      webseite: standort.webseite || this.i18n.t('p1.notAvailable'),
      bankName: standort.bank_name || this.i18n.t('p1.notAvailable'),
      bankIban: standort.bank_iban || this.i18n.t('p1.notAvailable'),
      bankBic: standort.bank_bic || this.i18n.t('p1.notAvailable'),
    };
  }

  private resolvePermissions(knowledge: KnowledgeData, akte: Record<string, unknown>): DashboardPermissions {
    const fromPermissions = knowledge.permissions;

    return {
      installmentAllowed: fromPermissions?.rzv_erlaubt ?? this.readBoolean(akte, ['rzv_erlaubt', 'installment_allowed']),
      settlementAllowed: fromPermissions?.vgl_erlaubt ?? this.readBoolean(akte, ['vgl_erlaubt', 'settlement_allowed']),
      deferralAllowed: fromPermissions?.std_erlaubt ?? this.readBoolean(akte, ['std_erlaubt', 'deferral_allowed']),
    };
  }

  private mapInvoices(rechnungen: RechnungData[]): DashboardInvoice[] {
    return rechnungen.map((invoice) => ({
      number: invoice.rechnungsnummer || this.i18n.t('p1.noNumber'),
      date: invoice.rechnungsdatum || '',
      amount: this.safeNumber(invoice.rechnungsbetrag),
      reason: invoice.forderungsgrund || this.i18n.t('p1.unknown'),
    }));
  }

  private mapTitleDetails(titel: TitelData[]): DashboardTitleDetail[] {
    return titel.map((title) => {
      const interestBearingCosts = this.safeNumber(title.titel_verzinsliche_kosten);
      const nonInterestBearingCosts = this.safeNumber(title.titel_unverzinsliche_kosten);

      return {
        designation: title.titelbezeichnung || title.titel_art || '',
        number: title.titelnummer || title.aktenzeichen_gericht || '',
        court: (title.titelgericht || title.gericht || title.court || '').trim(),
        date: title.titeldatum || title.titel_datum || '',
        amount: this.safeNumber(title.titelbetrag),
        mainClaim: this.safeNumber(title.titel_hauptforderung),
        costs: interestBearingCosts + nonInterestBearingCosts,
        interest: this.safeNumber(title.titel_zinsen),
      };
    });
  }

  private mapPayments(payments: ZahlungData[]): DashboardPayment[] {
    return payments.map((payment) => {
      const record = payment as unknown as Record<string, unknown>;

      return {
        amount: this.safeNumber(payment.zahlungsbetrag),
        date: payment.zahlungsdatum || '',
        reference: this.readString(record, ['bezeichnung', 'zahlungsart', 'buchungstext', 'event_code']),
      };
    });
  }

  private mapDetailedStatement(knowledge: KnowledgeData, akte: Record<string, unknown>): DashboardDetailedClaimLine[] {
    const details = knowledge.claim_details?.breakdown?.detailed;
    const rows = Array.isArray(details) ? details : this.parseDetailedStatementFallback(akte['forderungsaufstellung_detailliert_json']);

    return rows
      .map((row) => {
        const record = this.asRecord(row);
        const raw = this.asRecord(record['raw']);
        const source = { ...raw, ...record };
        const description = this.readString(source, ['description', 'bezeichnung', 'Bezeichnung', 'Bemerkung']);
        const amountValue = source['amount'] ?? source['betrag'] ?? source['Betrag'];
        const amount = parseFlexibleNumber(amountValue);
        const amountText = this.readString(source, ['amount_text', 'betrag', 'Betrag']);
        const date = this.readString(source, ['date', 'datum', 'Datum']);
        const mainClaim = this.mapDetailedAmount(source, 'main_claim_amount', 'main_claim_amount_text', 'Hauptforderung');
        const interest = this.mapDetailedAmount(source, 'interest_amount', 'interest_amount_text', 'Zinsen');
        const nonInterestCosts = this.mapDetailedAmount(
          source,
          'non_interest_costs_amount',
          'non_interest_costs_amount_text',
          'unverzinsl. Kosten'
        );
        const interestCosts = this.mapDetailedAmount(
          source,
          'interest_costs_amount',
          'interest_costs_amount_text',
          'verzinsl. Kosten'
        );
        const claimAmount = this.mapDetailedAmount(source, 'claim_amount', 'claim_amount_text', 'Forderung');
        const payment = this.mapDetailedAmount(source, 'payment_amount', 'payment_amount_text', 'Zahlung');
        const hasRawStructuredDetails = [
          'Hauptforderung',
          'Zinsen',
          'unverzinsl. Kosten',
          'verzinsl. Kosten',
          'Forderung',
          'Zahlung',
        ].some((key) => Object.prototype.hasOwnProperty.call(raw, key) || Object.prototype.hasOwnProperty.call(record, key));
        const hasStructuredDetails = hasRawStructuredDetails || [
          mainClaim,
          interest,
          nonInterestCosts,
          interestCosts,
          claimAmount,
          payment,
        ].some((entry) => entry.amount !== null || entry.text.length > 0);

        return {
          description,
          amount,
          amountText,
          date,
          mainClaim: mainClaim.amount,
          mainClaimText: mainClaim.text,
          interest: interest.amount,
          interestText: interest.text,
          nonInterestCosts: nonInterestCosts.amount,
          nonInterestCostsText: nonInterestCosts.text,
          interestCosts: interestCosts.amount,
          interestCostsText: interestCosts.text,
          claimAmount: hasStructuredDetails ? claimAmount.amount : amount,
          claimAmountText: hasStructuredDetails ? claimAmount.text : amountText,
          payment: payment.amount,
          paymentText: payment.text,
          hasStructuredDetails,
          raw: Object.keys(raw).length > 0 ? raw : record,
        };
      })
      .filter((row) => row.description.length > 0 || row.amount !== null || row.amountText.length > 0 || row.date.length > 0 || row.hasStructuredDetails);
  }

  private mapDetailedAmount(
    source: Record<string, unknown>,
    amountKey: string,
    textKey: string,
    rawKey: string
  ): { amount: number | null; text: string } {
    const value = source[amountKey] ?? source[rawKey];

    return {
      amount: parseFlexibleNumber(value),
      text: this.readString(source, [textKey, rawKey]),
    };
  }

  private parseDetailedStatementFallback(value: unknown): DetailedClaimStatementData[] {
    if (Array.isArray(value)) {
      return value.filter((row): row is DetailedClaimStatementData => !!row && typeof row === 'object');
    }

    if (typeof value !== 'string' || value.trim() === '') {
      return [];
    }

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((row): row is DetailedClaimStatementData => !!row && typeof row === 'object')
        : [];
    } catch {
      return [];
    }
  }

  private resolveInvoicePresentation(mainClaim: number, invoices: DashboardInvoice[]): DashboardInvoicePresentation {
    if (invoices.length === 0) {
      return {
        amountMode: 'open',
        sectionLabel: this.i18n.t('p1.partialInvoices'),
        sectionLabelKey: 'p1.partialInvoices',
        amountLabel: this.i18n.t('p1.openAmount'),
        amountLabelKey: 'p1.openAmount',
        note: '',
      };
    }

    const invoiceTotal = invoices.reduce((sum, invoice) => sum + Math.max(invoice.amount, 0), 0);
    const tolerance = Math.max(1, mainClaim * 0.02);
    const invoicesReconcileWithMainClaim = Math.abs(invoiceTotal - mainClaim) <= tolerance;

    if (invoicesReconcileWithMainClaim) {
      return {
        amountMode: 'open',
        sectionLabel: this.i18n.t('p1.partialInvoices'),
        sectionLabelKey: 'p1.partialInvoices',
        amountLabel: this.i18n.t('p1.openAmount'),
        amountLabelKey: 'p1.openAmount',
        note: '',
      };
    }

    return {
      amountMode: 'hidden_inconsistent',
      sectionLabel: this.i18n.t('p1.historicalInvoices'),
      sectionLabelKey: 'p1.historicalInvoices',
      amountLabel: '',
      note: this.i18n.t('p1.historicalNote'),
      noteKey: 'p1.historicalNote',
    };
  }

  private latestPayment(payments: ZahlungData[]): { amount: number; date: string } {
    if (payments.length === 0) {
      return { amount: 0, date: '' };
    }

    const sorted = [...payments].sort((a, b) => {
      const aDate = new Date(a.zahlungsdatum).getTime();
      const bDate = new Date(b.zahlungsdatum).getTime();
      return bDate - aDate;
    });

    return {
      amount: this.safeNumber(sorted[0].zahlungsbetrag),
      date: sorted[0].zahlungsdatum || '',
    };
  }

  private installmentSnapshot(rate: RateData | undefined, amountKey: keyof RateData, dateKey: keyof RateData): DashboardInstallmentSnapshot | null {
    if (!rate) {
      return null;
    }

    const amountRaw = rate[amountKey];
    const dateRaw = rate[dateKey];

    const amount = typeof amountRaw === 'number' ? amountRaw : this.safeNumber(amountRaw);
    const date = typeof dateRaw === 'string' ? dateRaw : '';

    if (amount <= 0 && date.length === 0) {
      return null;
    }

    return { amount, date };
  }

  private consistentInstallments(rate: RateData): DashboardActiveInstallmentPlan['installments'] | null {
    const countRaw = rate.anzahl_raten;
    if ((typeof countRaw !== 'number' && typeof countRaw !== 'string')
      || !/^[1-9]\d*$/.test(String(countRaw))) {
      return null;
    }
    const count = Number(countRaw);
    if (!Number.isSafeInteger(count) || count < 2) {
      return null;
    }

    // Validate technical calendar dates without timezone conversion or month rollover.
    const calendarDate = (value: unknown): number[] | null => {
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return null;
      }
      const [year, month, day] = value.split('-').map(Number);
      const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
      const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1]
        ? [year, month, day] : null;
    };
    const cents = (value: unknown): number | null => {
      if (typeof value !== 'number' && typeof value !== 'string') {
        return null;
      }
      const match = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/.exec(String(value));
      if (!match) {
        return null;
      }
      const fraction = (match[2] ?? '').padEnd(2, '0');
      const result = Number(match[1]) * 100 + Number(fraction);
      return Number.isSafeInteger(result) && result > 0
        && Number(value).toFixed(2) === `${match[1]}.${fraction}` ? result : null;
    };
    const first = calendarDate(rate.aktuelle_rate_datum);
    const last = calendarDate(rate.letzte_rate_datum);
    const amount = cents(rate.aktuelle_rate_hoehe);
    const finalAmount = cents(rate.letzte_rate_hoehe);
    if (!first || !last || amount === null || finalAmount === null
      || first[2] !== last[2] || (last[0] - first[0]) * 12 + last[1] - first[1] + 1 !== count) {
      return null;
    }

    const dates: string[] = [];
    for (let index = 0; index < count; index++) {
      const month = first[0] * 12 + first[1] - 1 + index;
      const date = `${Math.floor(month / 12).toString().padStart(4, '0')}-${(month % 12 + 1).toString().padStart(2, '0')}-${first[2].toString().padStart(2, '0')}`;
      if (!calendarDate(date)) {
        return null;
      }
      dates.push(date);
    }
    for (const [index, amountRaw, dateRaw] of [
      [1, rate.naechste_rate_hoehe, rate.naechste_rate_datum],
      [2, rate['uebernaechste_rate_hoehe'], rate['uebernaechste_rate_datum']],
      [count - 2, rate['vorletzte_rate_hoehe'], rate['vorletzte_rate_datum']],
    ] as const) {
      const dateAbsent = dateRaw === null || dateRaw === undefined || dateRaw === '';
      const amountAbsent = amountRaw === null || amountRaw === undefined || amountRaw === ''
        || amountRaw === 0 || amountRaw === '0' || amountRaw === '0.00';
      if (dateAbsent && amountAbsent) {
        continue;
      }
      if (!calendarDate(dateRaw) || dateRaw !== dates[index]
        || cents(amountRaw) !== (index === count - 1 ? finalAmount : amount)) {
        return null;
      }
    }
    return dates.map((date, index) => ({
      date, amount: (index === count - 1 ? finalAmount : amount) / 100,
      labelKey: index === 0 ? 'widget.rateCurrent' : index === count - 1 ? 'widget.rateLast' : 'caseView.records.rate',
      ...(index > 0 && index < count - 1 ? { labelParams: { index: index + 1 } } : {}),
    }));
  }

  // A delivered row means an agreement is running; inconsistent data keeps the named snapshots.
  private activeInstallmentPlan(rate: RateData | undefined): DashboardActiveInstallmentPlan | null {
    if (!rate) {
      return null;
    }

    const complete = this.consistentInstallments(rate);
    if (complete) {
      return { openInstallments: Number(rate.anzahl_raten), installments: complete };
    }

    const installments = ([
      ['widget.rateCurrent', 'aktuelle_rate_hoehe', 'aktuelle_rate_datum'],
      ['widget.rateNext', 'naechste_rate_hoehe', 'naechste_rate_datum'],
      ['widget.rateFollowing', 'uebernaechste_rate_hoehe', 'uebernaechste_rate_datum'],
      ['widget.ratePenultimate', 'vorletzte_rate_hoehe', 'vorletzte_rate_datum'],
      ['widget.rateLast', 'letzte_rate_hoehe', 'letzte_rate_datum'],
    ] as const).flatMap(([labelKey, amountKey, dateKey]) => {
      const snapshot = this.installmentSnapshot(rate, amountKey, dateKey);
      return snapshot ? [{ labelKey, ...snapshot }] : [];
    });

    // Hide the last row only when both date and amount match an earlier displayed rate.
    const last = installments[installments.length - 1];
    if (last?.labelKey === 'widget.rateLast'
      && installments.slice(0, -1).some((entry) => entry.date === last.date && entry.amount === last.amount)) {
      installments.pop();
    }

    return { openInstallments: this.safeNumber(rate.anzahl_raten), installments };
  }

  private normalizeProcedureStatus(value: string): DashboardProcedureStatus {
    const normalized = value.toLowerCase();
    if (normalized === 'vorgerichtlich' || normalized === 'nachgerichtlich' || normalized === 'gerichtlich' || normalized === 'ueberwachung') {
      return normalized;
    }
    return 'unknown';
  }

  private procedureLabel(status: DashboardProcedureStatus): string {
    if (status === 'vorgerichtlich') {
      return this.i18n.t('p1.preLitigation');
    }
    if (status === 'nachgerichtlich') {
      return this.i18n.t('p1.postJudgment');
    }
    if (status === 'gerichtlich') {
      return this.i18n.t('p1.litigation');
    }
    if (status === 'ueberwachung') {
      return 'Überwachung';
    }
    return this.i18n.t('p1.unknown');
  }

  private readNumber(source: Record<string, unknown>, keys: string[]): number {
    for (const key of keys) {
      const parsed = parseFlexibleNumber(source[key]);
      if (parsed !== null) {
        return parsed;
      }
    }
    return 0;
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  }

  private readBoolean(source: Record<string, unknown>, keys: string[]): boolean {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'number') {
        return value > 0;
      }
      if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
          return true;
        }
        if (normalized === 'false' || normalized === '0' || normalized === 'no') {
          return false;
        }
      }
    }
    return false;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object') {
      return value as Record<string, unknown>;
    }
    return {};
  }

  private safeNumber(value: unknown): number {
    return toSafeNumber(value);
  }

  private clampMoney(value: number): number {
    const rounded = Math.round(value * 100) / 100;
    return rounded > 0 ? rounded : 0;
  }
}
